/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { apiSlice } from "../apiSlice";
import { API, API_SLICE_TAGS } from "../../../globals/globals";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import { sanitizeQueryParams } from "../../helpers/helpers";
import {
  IArgs,
  IRadioNodes,
  INrCell,
  ILteCell,
  INgX2Connection,
  ILteX2Connection,
  IFullDeviceDetails,
  ILanDevice,
  IConfigAlert,
  IConfigStatus,
  IExternalAuthentication,
  IRfFeed,
  IRn620,
  IZoneArgs,
  IZoneDevice,
  IFiles,
  IFileArgs,
  ICellTypeArgs,
  ICellNameKpiCell,
} from "./types";
import { IPaginatedData } from "../devices/types";
// import { IArgs, IPaginatedData } from "./types";
// import { sanitizeQueryParams } from "../../helpers/helpers";

export const deviceConfigApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    setDeviceConfig: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.DEVICECONFIG.path}/${payload.deviceObjectKey}`,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getLanDevices: builder.query<IPaginatedData<ILanDevice>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.IPINTERFACE.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.IPINTERFACE],
    }),
    getRadioNodes: builder.query<IPaginatedData<IRadioNodes>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RADIONODES.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.RADIONODES],
    }),
    getNrCells: builder.query<IPaginatedData<INrCell>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.NRCELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.NRCELLS],
    }),
    getZoneCells: builder.query({
      query: (args: any) => ({
        url:
          args?.deviceNode === "SN"
            ? `${API.ENDPOINTS.LTEZONECELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
            : `${API.ENDPOINTS.NGZONECELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.ZONECELLS],
    }),

    getZoneList: builder.mutation<IPaginatedData<IZoneDevice[]>, IZoneArgs>({
      query: (args) => ({
        url:
          args?.deviceNode === "SN"
            ? `${API.ENDPOINTS.LTEZONECELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
            : `${API.ENDPOINTS.NGZONECELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    getLteCells: builder.query<IPaginatedData<ILteCell>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.LTECELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.LTECELLS],
    }),
    getNgX2Connections: builder.query<IPaginatedData<INgX2Connection>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.NGX2CONNECTIONS.path}/${args.deviceKey}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.NGX2CONNECTIONS],
    }),
    getLteX2Connections: builder.query<IPaginatedData<ILteX2Connection>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.LTEX2CONNECTIONS.path}/${args.deviceKey}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.LTEX2CONNECTIONS],
    }),
    getDeviceByKey: builder.query<IPaginatedData<IFullDeviceDetails>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DEVICEBYKEY.path}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.DEVICEBYKEY],
    }),
    getDeviceFullDetail: builder.mutation<IPaginatedData<IFullDeviceDetails>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DEVICEBYKEY.path}?${sanitizeQueryParams(args.filters)}`, method: "GET" }),
    }),
    getConfigAlerts: builder.query<Array<IConfigAlert>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.CONFIGALERTS.path}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.CONFIGALERTS],
    }),
    updateConfigAlerts: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.CONFIGALERTS.path}?${sanitizeQueryParams(payload.queryParam)}`,
        method: "PUT",
        body: { ackIds: payload.ackIds },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    getConfigStatus: builder.query<Array<IConfigStatus>, { [key: string]: string }>({
      query: (args) => ({
        url: `${API.ENDPOINTS.CONFIGSTATUS.path}/${args.deviceObjectKey}`,
        params: { userId: args.userId, tableId: args.tableId },
      }),
      providesTags: [API_SLICE_TAGS.CONFIGSTATUS],
    }),

    getExternalAuthentication: builder.query<IExternalAuthentication, { [key: string]: string }>({
      query: (args) => ({
        url: `${API.ENDPOINTS.EXTERNALAUTHENTICATION.path}`,
        params: { actionType: args.actionType },
      }),
      providesTags: [API_SLICE_TAGS.EXTERNALAUTHENTICATION],
    }),

    setExternalAuthentication: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.EXTERNALAUTHENTICATION.path}`,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    getRfFeeds: builder.query<IPaginatedData<IRfFeed>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RFFEEDS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.RADIONODES],
    }),

    getRn620s: builder.query<IPaginatedData<IRn620>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RN620S.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.RADIONODES],
    }),

    setRequestFullRefresh: builder.mutation({
      query: (deviceKey: any) => ({
        url: `${API.ENDPOINTS.REFRESHFULLDEVICE.path}/${deviceKey}`,
        method: "GET",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getRfFeedsLatest: builder.query<IPaginatedData<IRfFeed>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RFFEEDSLATEST.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.RFFEEDSLATEST],
    }),
    getDusList: builder.query<IPaginatedData<IRadioNodes>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.DULIST.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
    }),
    getNrCellList: builder.mutation<IPaginatedData<INrCell>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.NRCELLS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
      }),
    }),
    setRequestPartialRefresh: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.REFRESHPARTIALDEVICE.path}/${args.deviceKey}`,
        method: "POST",
        body: args.dataPathList,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getFileSystemList: builder.query<IPaginatedData<IFiles>, IFileArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.FILESYSTEMLIST.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
    }),
    getDownloadFile: builder.mutation({
      query: (args: IFileArgs) => ({
        url: `${API.ENDPOINTS.DOWNLOADFILE.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: (response) => {
          const contentType = response.headers.get('Content-Type');
          if (contentType && contentType.includes('application/octet-stream')) {
            return response.blob();
          } else {
            return response.text();
          }
        },
      }),
    }),
    getLteNrByCellName: builder.query<ICellNameKpiCell, ICellTypeArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.LTERNCELLNAME.path}/${args?.type}/${args?.deviceKey}/${args?.cellName}`,
      }),
      providesTags: [API_SLICE_TAGS.LTECELLS],
    }),
    getKpisByCellName: builder.mutation<ICellNameKpiCell, ICellTypeArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.LTERNCELLNAME.path}/${args?.type}/${args?.deviceKey}/${args?.cellName}`,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getLteCellById: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLCONFIGS.path}/getLteCell/${args?.deviceKey}/${args?.radioNodeId}`,
        method: "GET",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
            return { status: meta?.response?.status, ...data };
          },
    }),
    getNrCellById: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLCONFIGS.path}/getNrCell/${args?.deviceKey}/${args?.radioNodeId}`,
        method: "GET",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    setRequestPartialRefreshRn: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.PARTIALREFRESHRN.path}/${args.deviceKey}`,
        method: "POST",
        body: args.dataPathList,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    triggerDasDiscovery: builder.mutation({
      query: (args: string | { deviceKey: string; componentTargets?: Record<string, string>[] }) => {
        const isLegacy = typeof args === "string";
        const deviceKey = isLegacy ? args : args.deviceKey;
        return {
          url: `${API.ENDPOINTS.DASDISCOVERY.path}/${deviceKey}`,
          method: "POST",
          body: isLegacy
            ? undefined
            : {
                componentTargets: args.componentTargets,
              },
        };
      },
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
  }),
});

export const {
  useSetDeviceConfigMutation,
  useGetLanDevicesQuery,
  useGetRadioNodesQuery,
  useGetNrCellsQuery,
  useGetLteCellsQuery,
  useGetZoneCellsQuery,
  useGetZoneListMutation,
  useGetNgX2ConnectionsQuery,
  useGetLteX2ConnectionsQuery,
  useGetDeviceByKeyQuery,
  useGetDeviceFullDetailMutation,
  useGetConfigAlertsQuery,
  useUpdateConfigAlertsMutation,
  useGetConfigStatusQuery,
  useGetExternalAuthenticationQuery,
  useSetExternalAuthenticationMutation,
  useGetRfFeedsQuery,
  useGetRn620sQuery,
  useSetRequestFullRefreshMutation,
  useGetRfFeedsLatestQuery,
  useGetDusListQuery,
  useGetNrCellListMutation,
  useSetRequestPartialRefreshMutation,
  useGetFileSystemListQuery,
  useGetDownloadFileMutation,
  useGetLteNrByCellNameQuery,
  useGetLteCellByIdMutation,
  useGetKpisByCellNameMutation,
  useGetNrCellByIdMutation,
  useSetRequestPartialRefreshRnMutation,
  useTriggerDasDiscoveryMutation
} = deviceConfigApiSlice;
